import { v4 as uuidv4 } from 'uuid'
import { AddIcon, Text, TrashIcon } from 'components'
import { ContactOptionInput } from 'components/UserInputs/ContactOptionInput'
import { useBoolean, useClickOutside } from 'hooks'
import { useCallback, useRef } from 'react'
import { Box, Flex } from 'theme-ui'
import { ContactOption, TeacherContact, ViewMode } from 'types'

export type ContactDetailsSectionProps = {
  name: string
  setFieldValue: (field: string, value: any) => void
  disabled?: boolean
  viewMode?: ViewMode
  countryCode?: string
  contact: TeacherContact
  tabIndex: number
  error?: {
    [key: string]: { email?: string; phone?: string } | undefined
  }
  onBlur?: () => void
}
export const ContactDetailsSection = ({
  name,
  contact,
  setFieldValue,
  viewMode,
  countryCode,
  disabled,
  tabIndex,
  onBlur,
  error,
}: ContactDetailsSectionProps) => {
  const contactDetailsRef = useRef<HTMLDivElement>(null)
  const {
    default: defaultContact = { email: undefined, phone: undefined },
    ...rest
  } = contact
  const setContact = useCallback(
    (contact) => {
      setFieldValue(name, contact)
    },
    [name, setFieldValue]
  )
  const updateContact = (id: string, updated?: Partial<ContactOption>) => {
    const newContact = { ...contact, [id]: updated }
    setContact(newContact)
  }
  const removeContact = (id: string) => {
    const newContact = { ...contact }
    delete newContact[id]
    setContact(newContact)
  }

  const [isErrorShowing, showError, hideError] = useBoolean(false)
  const handleBlur = () => {
    showError()
    onBlur && onBlur()
  }
  useClickOutside(contactDetailsRef, showError)

  const createContactOption = () => {
    if (
      Object.values(contact).every(
        (contact) => contact?.email || contact?.phone
      )
    ) {
      const newId = uuidv4()
      updateContact(newId, {})
    }
    hideError()
  }

  return (
    <Box
      data-cy="contact-details-section"
      ref={contactDetailsRef}
      sx={{ mb: 10 }}
    >
      <Flex variant={`forms.section.${viewMode}`} sx={{ mt: 20, mb: 40 }}>
        {viewMode === 'edit' && (
          <Text
            variant="text.accent"
            sx={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
            }}
            onClick={createContactOption}
          >
            <AddIcon
              sx={{ display: 'inline-flex', alignItems: 'center', pr: '9px' }}
            />
            Add contact
          </Text>
        )}
      </Flex>
      <Flex variant={`forms.section.${viewMode}`}>
        <ContactOptionInput
          email={{
            name: 'defaultContactEmail',
            value: defaultContact.email,
            error: error && isErrorShowing ? error?.default?.email : undefined,
          }}
          phone={{
            name: 'defaultContactPhone',
            value: defaultContact.phone,
            error: error && isErrorShowing ? error?.default?.phone : undefined,
          }}
          disabled={disabled}
          countryCode={countryCode}
          onUpdateContact={(newContact) => {
            updateContact('default', newContact)
          }}
          tabIndex={tabIndex}
        />
      </Flex>
      {Object.entries(rest).map(([id, option], idx) => (
        <Flex
          key={id}
          variant={`forms.section.${viewMode}`}
          sx={{
            position: 'relative',
            mt: [20, -10],
          }}
        >
          <ContactOptionInput
            email={{
              name: 'altContactEmail_' + idx,
              value: option.email,
              error: error && isErrorShowing ? error[id]?.email : undefined,
            }}
            phone={{
              name: 'altContactPhone_' + idx,
              value: option.phone,
              error: error && isErrorShowing ? error[id]?.phone : undefined,
            }}
            disabled={disabled}
            countryCode={countryCode}
            onUpdateContact={(newContact) => {
              updateContact(id, newContact)
            }}
            onBlur={handleBlur}
            tabIndex={tabIndex ? tabIndex + (idx + 1) * 2 : undefined}
          />
          {viewMode === 'edit' && (
            <Box
              data-cy="remove-contact"
              onClick={() => {
                removeContact(id)
              }}
              sx={{
                position: ['unset', null, 'absolute'],
                left: 'calc(100% + 10px)', // position the left side at the right side of the parent box + the margin from the input
                top: 'calc(50% - 20px)', // half the height of the container - the height of the object
                color: 'textPrimary',
                cursor: 'pointer',
                fontWeight: 600,
                mb: 30,
                mt: [
                  error && isErrorShowing && error[id]?.phone ? 0 : -30,
                  error && isErrorShowing && error[id]?.email ? 0 : -30,
                  0,
                ],
                flexBasis: 230,
                display: 'flex',
              }}
            >
              <TrashIcon
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  pr: '5px',
                }}
              />{' '}
              Remove
            </Box>
          )}
        </Flex>
      ))}
    </Box>
  )
}
